import { graphql, Link, withPrefix } from 'gatsby';
import { useState, Fragment } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Container } from '../components/container';
import { Columns, Column } from '../components/columns';
import { Counter } from '../components/animated-counter';
import { Caret } from '../components/svgs/caret';
import Dropdown from 'react-dropdown';
import { transitionSettings } from '../lib/constants';
import { Vine } from '../components/vine';
import { useVisibleDelay } from '../hooks/use-delay';
import { AnimatedText } from '../components/animated-text';
import { Hero } from '../components/hero';
import { PieChart } from '../components/pie-chart';

const variants = {
  enter: (loanDirection) => ({
    x: loanDirection > 0 ? '100%' : '-100%',
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (loanDirection) => ({
    x: loanDirection < 0 ? '100%' : '-100%',
    opacity: 0,
  }),
};

const ALL_VALUE = 999;

export default function ProgramHighlights({ data }) {
  // LOANS
  const loans = data?.loans?.nodes.map((file) => file.childMarkdownRemark);

  const loanOptions = loans?.map((loan) => ({
    value: loan?.frontmatter?.order,
    label: loan?.frontmatter?.title,
  }));
  loanOptions.push({ value: ALL_VALUE, label: 'View All' });

  const [selectedLoan, setSelectedLoan] = useState(null);
  const [[prevLoan, loanDirection], setLoanDirection] = useState([-1, -1]);
  const loan = loans.find(
    (loan) => loan.frontmatter.order === selectedLoan?.value
  );
  const handleLoanSelect = (option) => {
    setLoanDirection([option.value, option.value - prevLoan]);
    setSelectedLoan(option);
  };

  // GRANTS
  const grants = data?.grants?.nodes.map((file) => file.childMarkdownRemark);
  const grantOptions = grants?.map((grant) => ({
    value: grant?.frontmatter?.order,
    label: grant?.frontmatter?.title,
  }));
  grantOptions.push({ value: ALL_VALUE, label: 'View All' });
  const [selectedGrant, setSelectedGrant] = useState(null);
  const [[prevGrant, grantDirection], setGrantDirection] = useState([-1, -1]);
  const grant = grants.find(
    (grant) => grant.frontmatter.order === selectedGrant?.value
  );
  const handleGrantSelect = (option) => {
    setGrantDirection([option.value, option.value - prevGrant]);
    setSelectedGrant(option);
  };

  const [ref, animateIntroSection] = useVisibleDelay();

  return (
    <>
      <Hero />
      <article id="program-highlights" className="page wide">
        <Container>
          <h1 ref={ref} className="undervine">
            <span>
              <AnimatedText isVisible={animateIntroSection}>
                2020-2021 Program Highlights
              </AnimatedText>
              {animateIntroSection && <Vine isWide />}
            </span>
          </h1>

          <p>
            The Northland Foundation staff and our many community and funding
            partners worked hard this past fiscal year to deliver critical
            resources and support the seeds of growth in communities throughout
            our region. Following are some of the highlights from July 1, 2020
            through June 30, 2021.
          </p>
          <h3 className="undervine">
            <span>
              Overall Organization
              <Vine />
            </span>
          </h3>
          <ul>
            <li>
              Raised and managed<strong> nearly $10 million</strong> to fund
              grantmaking, business services, KIDS PLUS, special initiatives,
              and COVID response efforts. See a{' '}
              <a
                href={withPrefix(`/nof_funding_snapshot_2021.pdf`)}
                target="_blank"
              >
                Funding Snapshot
              </a>{' '}
              for more details.
            </li>

            <li>
              Officially <strong>launched </strong>
              <a
                href="https://northlandfdn.org/special-initiatives/maada-ookiing.php"
                target="_blank"
                rel="noopener noreferrer"
              >
                Maada’ookiing
              </a>
              , with guidance from the{' '}
              <a
                href="https://northlandfdn.org/special-initiatives/maada-ookiing-our-story.php"
                target="_blank"
                rel="noopener noreferrer"
              >
                Indigenous-led design team
              </a>{' '}
              and{' '}
              <a
                href="https://northlandfdn.org/special-initiatives/maada-ookiing-indigenous-led.php"
                target="_blank"
                rel="noopener noreferrer"
              >
                Board
              </a>
              , <strong>awarding the </strong>
              <a
                href="https://northlandfdn.org/news/news-releases/maada-ookiing-grants-pr-6.2021.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                first 13 grants
              </a>{' '}
              totaling $32,300 to individuals to strengthen and support
              Indigenous communities.
            </li>

            <li>
              Selected as a{' '}
              <a
                href="https://northlandfdn.org/special-initiatives/community-resource-hub.php"
                target="_blank"
                rel="noopener noreferrer"
              >
                Community Resource Hub
              </a>{' '}
              for the{' '}
              <a
                href="https://mn.gov/dhs/media/news/#/detail/appId/1/id/473317"
                target="_blank"
                rel="noopener noreferrer"
              >
                Minnesota Department of Human Services
              </a>
               to work with six regional partners 
              <strong>
                helping pregnant and parenting families of young children
              </strong>{' '}
              connect with services and supports.
            </li>
          </ul>
        </Container>
        <Container isDark className="program-highlights__dropdown-section">
          <div className="program-highlights__select">
            <h2 className="undervine h3">
              <span>
                Grant Program
                <Vine />
              </span>
            </h2>

            <ul>
              <li>
                Made a record{' '}
                <strong>644 grants totaling over $7.5 million </strong>to
                benefit the people and communities of the region.
              </li>

              <li>
                Disbursed over{' '}
                <strong>$4.6 million in grants dedicated directly to </strong>
                <Link to="/covid-response">COVID relief efforts</Link> in
                northeastern Minnesota, $3.8 million of which went to Small
                Business Relief Grant recipients.
              </li>

              <li>
                Advanced the work of our{' '}
                <a
                  href="https://northlandfdn.org/special-initiatives/rural-aging.php"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Integrated Rural Community Aging Program
                </a>{' '}
                to{' '}
                <strong>
                  help older adults and family caregivers, investing $1.6
                  million
                </strong>{' '}
                in funding and technical support for regional partners in the
                areas of Community Planning and Care Integration, AGE to
                age/Social Connectedness, and Quality of Care/Workforce.
              </li>
            </ul>

            <PieChart />

            <label>View Grants</label>
            <p>
              Use the drop-down to view non-COVID related grants awarded between
              July 1, 2020 and June 30, 2021. COVID-19 response grants may be
              viewed <Link to="/covid-response">here.</Link>
            </p>

            {grantOptions && (
              <div className="Dropdown">
                <Dropdown
                  value={selectedGrant}
                  onChange={handleGrantSelect}
                  options={grantOptions}
                  placeholder={'Select'}
                />
                <Caret />
              </div>
            )}
          </div>

          <AnimatePresence initial={false} custom={grantDirection}>
            {selectedGrant?.value === ALL_VALUE ? (
              <motion.div
                className="program-highlights__detail"
                key={'all'}
                custom={grantDirection}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ ...transitionSettings }}
              >
                <h3 className="undervine">
                  <span>
                    All Grants
                    <Vine />
                  </span>
                </h3>
                {grants.map((g) => (
                  <Fragment key={g.id}>
                    <h4>{g.frontmatter.title}</h4>
                    <div
                      className="grid small"
                      dangerouslySetInnerHTML={{ __html: g.html }}
                    />
                  </Fragment>
                ))}
              </motion.div>
            ) : (
              grant && (
                <motion.div
                  className="program-highlights__detail"
                  key={grant.id}
                  custom={grantDirection}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ ...transitionSettings }}
                >
                  <h3 className="undervine">
                    <span>
                      {grant.frontmatter.title}
                      <Vine />
                    </span>
                  </h3>

                  <div
                    className="grid"
                    dangerouslySetInnerHTML={{ __html: grant.html }}
                  />
                </motion.div>
              )
            )}
          </AnimatePresence>
        </Container>

        <Container>
          <h2 className="undervine h3">
            <span>
              Business Services
              <Vine />
            </span>
          </h2>

          <ul>
            <li>
              Received <strong>grants for loan capital </strong>to help start
              and sustain businesses including $2.3 million from the{' '}
              <a
                href="https://www.cdfifund.gov/Pages/default.aspx"
                target="_blank"
                rel="noopener noreferrer"
              >
                CDFI Fund
              </a>{' '}
              and Rapid Response Program.
            </li>

            <li>
              Supported 9{' '}
              <a
                href="https://northlandsbdc.org/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Northland SBDC
              </a>{' '}
              consultants to serve <strong>1,049 clients</strong>, assist with{' '}
              <strong>51 start-ups</strong>, and <strong>help clients</strong>{' '}
              <strong>earn $16.3 million</strong> in business capital.
            </li>

            <li>
              Closed loans to help finance{' '}
              <strong>4 new businesses and 5 expansions</strong> in northeastern
              Minnesota.
            </li>
          </ul>

          <Columns className="program-highlights__counters">
            <Column className="auto">
              <span className="label">Total Loans</span>
              <h3 className="display-value">
                <Counter from={0} to={9} width={30} /> Business Loans
              </h3>
              <span className="label">(4 Start-ups, 5 Expansions)</span>
            </Column>
            <Column className="auto">
              <span className="label">Dollar Total</span>
              <h3 className="display-value">
                $
                <Counter from={100000} to={789723} width={180} duration={1.6} />
              </h3>
            </Column>
            <Column className="auto">
              <span className="label">Dollars Leveraged</span>
              <h3 className="display-value">
                $<Counter from={100000} to={4149730} width={227} duration={2} />
              </h3>
            </Column>
          </Columns>
          <div className="program-highlights__dropdown-section">
            <div className="program-highlights__select">
              <label>View Loans</label>
              <p>
                Use the drop-down to view loans closed July 1, 2020, through
                June 30, 2021.
              </p>

              {loanOptions && (
                <div className="Dropdown">
                  <Dropdown
                    value={selectedLoan}
                    onChange={handleLoanSelect}
                    options={loanOptions}
                    placeholder={'Select'}
                  />
                  <Caret />
                </div>
              )}
            </div>
            <AnimatePresence initial={false} custom={loanDirection}>
              {selectedLoan?.value === ALL_VALUE ? (
                <motion.div
                  className="program-highlights__detail"
                  key={'all'}
                  custom={loanDirection}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ ...transitionSettings }}
                >
                  <h3 className="undervine">
                    <span>
                      All Loans
                      <Vine />
                    </span>
                  </h3>
                  {loans.map((l) => (
                    <Fragment key={l.id}>
                      <h4>{l.frontmatter.title}</h4>
                      <div dangerouslySetInnerHTML={{ __html: l.html }} />
                    </Fragment>
                  ))}
                </motion.div>
              ) : (
                loan && (
                  <motion.div
                    className="program-highlights__detail"
                    key={loan.id}
                    custom={loanDirection}
                    variants={variants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ ...transitionSettings }}
                  >
                    <h3 className="undervine">
                      <span>
                        {loan.frontmatter.title}
                        <Vine />
                      </span>
                    </h3>
                    <div dangerouslySetInnerHTML={{ __html: loan.html }} />
                  </motion.div>
                )
              )}
            </AnimatePresence>
          </div>
        </Container>
        <Container isDark className="kids-plus">
          <h2 className="undervine h3">
            <span>
              KIDS PLUS Program
              <Vine />
            </span>
          </h2>

          <ul>
            <li>
              Administered{' '}
              <strong>$2.3 million in Minnesota Department of Education</strong>{' '}
              <a
                href="https://northlandfdn.org/special-initiatives/index.php"
                target="_blank"
                rel="noopener noreferrer"
              >
                Early Learning Scholarships
              </a>{' '}
              to help families of young children afford high-quality early care
              and education.
            </li>

            <li>
              Delivered the{' '}
              <a
                href="https://northlandfdn.org/kids-plus/youth-programs.php"
                target="_blank"
                rel="noopener noreferrer"
              >
                KIDS PLUS Youth In Philanthropy Program
              </a>
              , a grantmaking board of five adult mentors and{' '}
              <strong>
                15 high school students who awarded 15 grants totaling $14,610{' '}
              </strong>
              to support youth-led projects, with funding support from our
              Emeritus Trustee Group.
            </li>

            <li>
              Completed the{' '}
              <a
                href="https://www.northlandfdn.org/news/notes/2021-02/youth-development-programs.shtml"
                target="_blank"
                rel="noopener noreferrer"
              >
                Twin Ports Youth Leadership Academy service project,
              </a>{' '}
              which had been cut short in March 2020 due to the pandemic. All 12
              adult mentors and 32 of the <strong>youth leaders </strong>
              reconnected in the fall to implement the project.
            </li>

            <li>
              Supported intergenerational programming in{' '}
              <a
                href="https://northlandfdn.org/files/kids-plus-age2age-about.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                18 AGE to age sites
              </a>{' '}
              in our region to reduce social isolation while maintaining health
              and safety. Sites reported{' '}
              <strong>engaging a total of 9,100</strong>{' '}
              <strong>youth, older adults, and ages-in-between</strong> last
              year.
            </li>

            <li>
              Hosted{' '}
              <strong>
                46 virtual trainings to help 875 participating child care
                providers
              </strong>{' '}
              enhance program quality and achieve their Parent Aware Star
              Rating.
            </li>

            <li>
              Provided{' '}
              <strong>
                34 virtual trainings and learning community meetings for 1,637
                service providers
              </strong>{' '}
              working with young children and families, through our{' '}
              <a
                href="https://northlandfdn.org/kids-plus/index.php"
                target="_blank"
                rel="noopener noreferrer"
              >
                Thrive and Early Childhood Initiatives
              </a>
              , to share strategies to foster resiliency and social-emotional
              wellbeing.
            </li>
          </ul>
        </Container>
      </article>
    </>
  );
}

export const pageQuery = graphql`
  query {
    loans: allFile(
      filter: { sourceInstanceName: { eq: "loans" }, extension: { eq: "md" } }
      sort: { fields: childrenMarkdownRemark___frontmatter___order, order: ASC }
    ) {
      nodes {
        childMarkdownRemark {
          id
          html
          fields {
            slug
          }
          frontmatter {
            order
            title
          }
        }
      }
    }
    grants: allFile(
      filter: { sourceInstanceName: { eq: "grants" }, extension: { eq: "md" } }
      sort: { fields: childrenMarkdownRemark___frontmatter___order, order: ASC }
    ) {
      nodes {
        childMarkdownRemark {
          id
          html
          fields {
            slug
          }
          frontmatter {
            order
            title
          }
        }
      }
    }
  }
`;
